import React, { useCallback, useEffect, useState } from "react";
import { Col, Divider, Form, Modal, Row, Space, Spin } from "antd";
import { makeStyles } from "@material-ui/core/styles";
import CustomButton from "components/Common/buttons/CustomButton";
import {
  createCustomerGroup,
  getCustomerGroupUploadUrl,
  updateCustomerGroup,
  uploadDataToS3BucketByUrl,
} from "utils/api";
import NGIForm from "components/Common/form/NGIForm";
import Title from "antd/lib/typography/Title";
import NGIDatePicker from "components/Common/inputs/NGIDatePicker";
import NGIFormItem from "components/Common/form/NGIFormItem";
import { defaultNgiErrorMethod, errorMessage, successMessage, warnMessage } from "components/Common/responses/message";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import { isEmpty } from "lodash";
import moment from "moment";
import NGIInput from "components/Common/inputs/NGIInput";
import closeIcon from "assets/images/svg/closeSmall.svg";
import Dragger from "antd/lib/upload/Dragger";
import uploadArea from "assets/images/svg/uploadArea.svg";
import * as xlsx from "xlsx";
import { VALIDATION_RULES } from "pages/price-management/CustomerPriceGroups/variables";
import { customDateFormatWithoutTime } from "components/Common/formatter";
import CustomLink from "components/Common/buttons/CustomLink";
import { generateCSV } from "utils/helpers/functions";
import { CSV_TEMPLATES } from "utils/constants";

const useStyles = makeStyles({
  modal: {
    maxWidth: "900px!important",
  },
  infoContainer: {
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    fontWeight: 600,

    "&>span:first-of-type": {
      fontSize: 13,
      fontWeight: 300,
    },
  },
  formItem: {
    display: "flex",

    "& .ant-col-12 + .ant-col-12": {
      paddingLeft: 10,
    },
  },
  disabled: {
    pointerEvents: "none",
    opacity: 0.6,
  },
  fileContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 15,
    border: "1px solid rgba(0, 0, 0, 0.05)",
    padding: "5px 10px",
    marginTop: 10,
  },
  removeBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 32,
    width: 32,
    borderRadius: 50,
    background: "rgba(255, 255, 255, 1)",

    "&:hover": {
      cursor: "pointer",
      opacity: 0.6,
    },
  },
  fileTitle: {
    fontWeight: 600,
    fontSize: 14,
  },
  titleContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
});

const NewCustomerGroupModal = ({ data, onClose, update }) => {
  const classes = useStyles();
  const [form] = Form.useForm();
  const isNew = isEmpty(data);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadedData, setUploadedData] = useState("");

  useEffect(() => {
    if (!isNew) {
      form.setFieldsValue({
        ...data,
        activationDate: moment(data?.activationDate),
        deactivationDate: data?.deactivationDate ? moment(data?.deactivationDate) : null,
      });
    }
  }, []);

  const onSave = useCallback(
    async values => {
      if (!uploadedData && isNew) {
        warnMessage("Please upload file");
        return;
      }
      let result;
      setIsLoading(true);
      const preparedData =
        !isNew && data.status === "Active"
          ? { deactivationDate: customDateFormatWithoutTime(values?.deactivationDate) }
          : {
              ...(!isNew && data),
              ...values,
              activationDate: customDateFormatWithoutTime(values.activationDate),
              deactivationDate: customDateFormatWithoutTime(values.deactivationDate),
            };

      if (isNew) {
        result = await getCustomerGroupUploadUrl().then(({ data: { result } }) =>
          uploadDataToS3BucketByUrl(result.url, uploadedData).then(() => result)
        );
      }
      const request = isNew ? createCustomerGroup : updateCustomerGroup;
      request(isNew ? { ...preparedData, csvFilename: result.fileName } : preparedData, data?.id)
        .then(() => {
          successMessage(`Customer group was ${isNew ? "created" : "updated"} successfully`);
          setIsLoading(false);
          setTimeout(() => update && update(), 1500);
          onClose();
        })
        .catch(e => {
          setIsLoading(false);
          defaultNgiErrorMethod(e);
        });
    },
    [uploadedData, isNew, data]
  );

  const openConfirmCloseModal = useCallback(() => setIsConfirmCloseOpen(true), []);
  const closeConfirmCloseModal = useCallback(() => setIsConfirmCloseOpen(false), []);

  const openConfirmationModal = useCallback(() => setIsConfirmationOpen(true), []);
  const closeConfirmationModal = useCallback(() => setIsConfirmationOpen(false), []);

  const readUploadFile = useCallback(e => {
    const file = e.file;
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = e => {
        const data = e.target.result;
        const workbook = xlsx.read(data);
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = xlsx.utils.sheet_to_json(worksheet, { raw: false });
        const csv = xlsx.utils.sheet_to_csv(worksheet, { raw: false });

        const formattedResult = json.map(row => ({ ...row }));
        const noError = formattedResult.every(item => item.customerNumber || null);
        !noError && errorMessage("Please upload the correct ‘*.csv | *.xls’ file with 'customerNumber' field");
        if (noError) {
          setUploadedData(csv);
          setFile(file);
        } else {
          setUploadedData("");
          setFile(null);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }, []);

  const removeFile = useCallback(() => {
    setFile(null);
    setUploadedData([]);
  }, []);

  return (
    <Modal
      title={
        <div className={classes.infoContainer}>
          {isNew ? "Create" : "Edit"} Customer Group
          <span>Please fill in all mandatory fields</span>
        </div>
      }
      className={classes.modal}
      open
      onCancel={openConfirmCloseModal}
      closable={false}
      footer={[]}
    >
      <Spin spinning={isLoading}>
        <NGIForm name="newCustomerGroup" form={form} onFinish={onSave}>
          <Divider />
          <Row className={classes.formItem}>
            <Col span={12}>
              <Title level={5}>Customer Group Name*</Title>
              <NGIFormItem name="name" rules={VALIDATION_RULES}>
                <NGIInput disabled={!isNew && data?.status === "Active"} placeholder="Customer Group Name" />
              </NGIFormItem>
            </Col>
            <Col span={12}>
              <Title level={5}>Owner</Title>
              <NGIFormItem name="owner">
                <NGIInput disabled={!isNew && data?.status === "Active"} placeholder="Owner" />
              </NGIFormItem>
            </Col>
          </Row>
          <Row className={classes.formItem}>
            <Col span={12}>
              <Title level={5}>Active From*</Title>
              <NGIFormItem name="activationDate" rules={VALIDATION_RULES}>
                <NGIDatePicker
                  disabled={!isNew && data?.status === "Active"}
                  allowClear
                  disableDates={current => moment() >= current}
                />
              </NGIFormItem>
            </Col>
            <Col span={12}>
              <Title level={5}>Active To</Title>
              <NGIFormItem name="deactivationDate">
                <NGIDatePicker
                  allowClear
                  disableDates={current =>
                    form.getFieldValue("activationDate")?.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }) >
                    current
                  }
                />
              </NGIFormItem>
            </Col>
          </Row>
          {isNew && (
            <>
              <Divider />
              <Row className={classes.formItem}>
                <Col span={24}>
                  <div className={classes.titleContainer}>
                    <Title level={5}>Customers List (Supports *.CSV, *.XLS)*</Title>
                    <CustomLink color="#0085FF" onClick={() => generateCSV(CSV_TEMPLATES.NEW_CUSTOMER_GROUP)}>
                      Download *.csv template
                    </CustomLink>
                  </div>

                  {file ? (
                    <div className={classes.fileContainer}>
                      <div className={classes.fileTitle}>{`${file.name}`}</div>
                      <div onClick={removeFile} className={classes.removeBtn}>
                        <img src={closeIcon} alt="remove" />
                      </div>
                    </div>
                  ) : (
                    <Dragger accept=".csv,.xls" showUploadList={false} customRequest={readUploadFile} name="file">
                      <img className="upload-bg" src={uploadArea} alt="upload" />
                      <p className="ant-upload-text">Click or drag file to this area to upload</p>
                      <p className="ant-upload-hint">
                        Support for a single upload. Strictly prohibit from uploading company data or other band files
                      </p>
                    </Dragger>
                  )}
                </Col>
              </Row>
            </>
          )}
        </NGIForm>
      </Spin>
      <Divider />
      <br />
      <Space>
        <CustomButton onClick={openConfirmCloseModal} type="primary" size="small" color="outlined" text="Cancel" />
        <CustomButton type="primary" onClick={openConfirmationModal} text={isNew ? "Create" : "Save"} size="small" />
      </Space>

      {isConfirmationOpen && (
        <ConfirmationModal
          description={`Are you sure you want to ${isNew ? "create" : "edit"} customer group?`}
          onCancel={closeConfirmationModal}
          onConfirm={form.submit}
          loading={isLoading}
        />
      )}
      {isConfirmCloseOpen && (
        <ConfirmationModal
          description={
            isNew
              ? "Customer Group creating is in progress. Are you sure you want to close it?"
              : "Customer Group editing is in progress. Are you sure you want to close it?"
          }
          onCancel={closeConfirmCloseModal}
          onConfirm={onClose}
        />
      )}
    </Modal>
  );
};

export default NewCustomerGroupModal;
